import type { Container, VM, Manifest, StorageStats, SystemStats, APIResponse } from '../types'

const API_BASE = '/api/v1'

// --- Session token (JWT) ---------------------------------------------------
// The control plane issues an HS256 JWT on /auth/login + /auth/signup. We keep
// it in localStorage so a refresh survives, and attach it as a Bearer header on
// every authed request. onUnauthorized lets the AuthProvider clear state + bounce
// to /login when a token expires (12h TTL) or is rejected.
const TOKEN_KEY = 'voltvisor.token'

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn
}

function authHeaders(extra?: HeadersInit): HeadersInit {
  const h: Record<string, string> = { 'Content-Type': 'application/json', ...(extra as Record<string, string>) }
  const t = getToken()
  if (t) h['Authorization'] = `Bearer ${t}`
  return h
}

// request() is for the ENVELOPED API surface ({success,data,error}) — containers,
// vms, storage, etc. Auth endpoints return a bare body and use rawRequest below.
async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: authHeaders(options?.headers),
  })

  if (response.status === 401) {
    if (onUnauthorized) onUnauthorized()
    throw new Error('Session expired — please sign in again')
  }

  const data: APIResponse<T> = await response.json()

  if (!data.success) {
    throw new Error(data.error || 'Request failed')
  }

  return data.data as T
}

// rawRequest() is for the AUTH surface, which returns a bare JSON body ({token,user})
// rather than the {success,data} envelope. On error it surfaces the `error` field.
async function rawRequest<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  })
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error || `Request failed (${response.status})`)
  }
  return data as T
}

// --- Auth ------------------------------------------------------------------
export interface AuthUser {
  id: string
  email: string
  org_id: string
  role: string
}
export interface LoginResponse {
  token: string
  user: AuthUser
}
export interface SignupResponse {
  token: string
  user: AuthUser
  org: { id: string; name: string; plan: string }
}
export interface MeResponse {
  user_id: string
  org_id: string
  email: string
  role: string
  is_superadmin: boolean
  capabilities: Record<string, unknown>
  plan?: { id: string; name: string; limits?: { max_vms?: number } }
  org?: { id: string; name: string; plan: string; dedicated?: boolean }
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  return rawRequest<LoginResponse>('/auth/login', { email, password })
}

export async function signup(orgName: string, email: string, password: string, plan = 'free'): Promise<SignupResponse> {
  return rawRequest<SignupResponse>('/auth/signup', { org_name: orgName, email, password, plan })
}

export async function getMe(): Promise<MeResponse> {
  const response = await fetch(`${API_BASE}/me`, { headers: authHeaders() })
  if (response.status === 401) {
    if (onUnauthorized) onUnauthorized()
    throw new Error('Session expired')
  }
  if (!response.ok) throw new Error(`Failed to load session (${response.status})`)
  return response.json()
}

// System
export async function getSystemStats(): Promise<SystemStats> {
  return request<SystemStats>('/stats')
}

export async function getHealth(): Promise<Record<string, unknown>> {
  const response = await fetch(`${API_BASE}/health`)
  return response.json()
}

export async function getVersion(): Promise<Record<string, string>> {
  return request<Record<string, string>>('/version')
}

// Containers
export async function getContainers(): Promise<Container[]> {
  return request<Container[]>('/containers')
}

export async function getContainer(id: string): Promise<Container> {
  return request<Container>(`/containers/${encodeURIComponent(id)}`)
}

export async function createContainer(manifestId: string, options?: { name?: string; autoStart?: boolean }): Promise<Container> {
  return request<Container>('/containers', {
    method: 'POST',
    body: JSON.stringify({
      manifest_id: manifestId,
      name: options?.name,
      auto_start: options?.autoStart,
    }),
  })
}

export async function startContainer(id: string): Promise<void> {
  await request(`/containers/${encodeURIComponent(id)}/start`, { method: 'POST' })
}

export async function stopContainer(id: string): Promise<void> {
  await request(`/containers/${encodeURIComponent(id)}/stop`, { method: 'POST' })
}

export async function restartContainer(id: string): Promise<void> {
  await request(`/containers/${encodeURIComponent(id)}/restart`, { method: 'POST' })
}

export async function destroyContainer(id: string): Promise<void> {
  await request(`/containers/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function getContainerLogs(id: string, lines = 100): Promise<string[]> {
  const data = await request<{ logs: string[] }>(`/containers/${encodeURIComponent(id)}/logs?lines=${lines}`)
  return data.logs
}

// VMs
export async function getVMs(): Promise<VM[]> {
  return request<VM[]>('/vms')
}

export async function getVM(id: string): Promise<VM> {
  return request<VM>(`/vms/${encodeURIComponent(id)}`)
}

export async function createVM(config: {
  id: string
  name?: string
  kernel_profile?: string
  rootfs: string
  memory?: string
  cpu_shares?: number
  auto_start?: boolean
}): Promise<VM> {
  return request<VM>('/vms', {
    method: 'POST',
    body: JSON.stringify(config),
  })
}

export async function startVM(id: string): Promise<void> {
  await request(`/vms/${encodeURIComponent(id)}/start`, { method: 'POST' })
}

export interface VMUsage {
  org_id: string
  active_seconds?: number
  parked_seconds?: number
  vm_count?: number
}
export async function getVMUsage(): Promise<VMUsage> {
  return request<VMUsage>('/vms/usage')
}

export async function stopVM(id: string): Promise<void> {
  await request(`/vms/${encodeURIComponent(id)}/stop`, { method: 'POST' })
}

export async function destroyVM(id: string): Promise<void> {
  await request(`/vms/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

// Storage
export async function getStorageStats(): Promise<StorageStats> {
  return request<StorageStats>('/storage/stats')
}

export async function getManifests(): Promise<Manifest[]> {
  return request<Manifest[]>('/storage/manifests')
}

export async function getManifest(id: string): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(`/storage/manifests/${encodeURIComponent(id)}`)
}

export async function getTinyVols(): Promise<Array<{
  id: string
  path: string
  file_count: number
  size: number
  modified_at: string
}>> {
  return request('/storage/tinyvols')
}

export async function cleanupStorage(dryRun = true): Promise<{
  dry_run: boolean
  orphaned_count: number
  freed_bytes: number
}> {
  return request(`/storage/cleanup?dry_run=${dryRun}`, { method: 'POST' })
}
